import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import WithEscNavigation from '@hobs/hocs/WithEscNavigation';
import WithRoles from '@hobs/hocs/WithRoles';
import Panel from '@hobs/components/Panel';
import File from '@hobs/components/File';
import objectPath from 'object-path';
import ShortId from 'shortid';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import Toast from '@hobs/components/Toast';
import Tooltip from '@hobs/components/Tooltip';
import { H2 } from '@hobs/components/Headings';
import { ServiceRequestNavContext } from '@hobs/components/ServiceRequestNavContext';
import InfoPanel from '@hobs/components/InfoPanel';
import InputLabel from '@hobs/components/InputLabel';
import Status from '@hobs/components/Status';
import Input from '@hobs/components/Input';
import Icon from '@hobs/components/Icon';
import Switch from '@hobs/components/Switch';
import Dropdown from '@hobs/components/Dropdown';
import BluePanel from '@hobs/components/BluePanel';
import Money from '@hobs/components/Money';
import Confirm from '@hobs/components/Confirm';
import FieldHistoryMessage from '@hobs/components/FieldHistoryMessage';
import { Formik, Field, Form } from 'formik';
import { GhostButton, RaisedButton } from '@hobs/components/Buttons';
import {
  DATE_FORMAT,
  USER_ROLES,
  SERVICE_REQUEST_NAVIGATION_ORIGIN
} from '@hobs/utils/constants';
import { format } from '@hobs/utils/formatWithTimeZone';
import * as Yup from 'yup';

import {
  splittedRemove,
  splittedResend,
  updateSplitted,
  forSubmit,
  getAssignmentServiceRequest,
  splitAssignment,
  splitAndSendAssignment,
  splittedUndo,
  updateAssignmentResponse,
  clearAssignmentServiceRequest,
  updateAssignmentDelivery,
  updateAssignmentDeliveryFinalizedStatus,
  rollbackDelivery,
  checkResponseSuggestion,
  appoint,
  removeAppointment
} from '@hobs/actions/assignmentServiceRequest';
import { calculateByDays } from '@hobs/actions/serviceRequests';
import PageHeader from '@hobs/components/PageHeader';

import CalculateByDays from '@hobs/utils/calculateByDays';
import CalcByDays from '@hobs/components/CalculateByDays';
import { uploadAttachments, clearAttachments } from '@hobs/actions/attachment';
import Quantity from '@hobs/components/Quantity';
import AssignmentResponse from '../../components/AssignmentResponse';
import './RequestInformation.scss';
import { PageLinks } from './PageLinks';

const ASSIGNMENT_SHAPE = {
  new: true,
  expanded: false,
  status: 'New',
  statusGridName: 'New',
  statusId: 0,
  parentServiceRequestId: '',
  subcontractorId: null,
  subcontractorName: null,
  requestedQuantity: '',
  deliveryQuantity: '',
  unitPrice: '',
  totalPrice: '',
  purchaseOrderNumber: '',
  positionNumber: '',
  myComment: '',
  subcontractorComment: '',
  vatValue: '',
  gross: '',
  canBeRemovedOrEdited: true,
  attachments: []
};

class RequestInformation extends PureComponent {
  state = {
    assignments: [],
    item: {},
    dataState: { take: 10, skip: 0, filter: {} },
    responseFileDialogOpen: false,
    deliveryFileDialogOpen: false,
    hasAppointment: false
  };

  constructor(props) {
    super(props);
    const { t } = props;
    this.form = React.createRef();

    this.assignmentValidationSchema = Yup.object().shape({
      subcontractorId: Yup.string()
        .required(t('Subcontractor is required'))
        .nullable(),
      requestedQuantity: Yup.number()
        .min(0, t('Invalid amount'))
        .required(t('Req. q-ty is required'))
    });

    this.deliveryValidationSchema = Yup.object().shape({});
  }

  componentDidMount() {
    this.loadComponentData();
  }

  componentDidUpdate(prevProps) {
    const { match } = this.props;
    if (
      match.params.serviceRequestId !== prevProps.match.params.serviceRequestId
    ) {
      this.loadComponentData();
    }
  }

  loadComponentData = () => {
    const { match } = this.props;

    this.props.forSubmit(match.params.serviceRequestId);
    this.getAssignmentServiceRequest();
  };

  componentWillUnmount() {
    this.props.clearAssignmentServiceRequest();
    this.props.clearAttachments();
  }

  navigateBack = navigationContext => () => {
    const { history } = this.props;

    const backUrl = this.getBackNavigationUrl(navigationContext);
    history.push(backUrl);
  };

  getAssignmentServiceRequest() {
    const { getAssignmentServiceRequest, match } = this.props;

    return getAssignmentServiceRequest(match.params.serviceRequestId).then(
      response => {
        const { item } = this.props;
        this.calculateByDays(item);
        this.setState({
          assignments: this.getAssignmentsItems(item.splittedServiceRequests),
          item: item
        });

        if (item.appointedUnitId) {
          this.openAppointDialog();
        }

        return response;
      }
    );
  }

  calculateByDays(values) {
    const data = CalculateByDays.calculate(
      {
        ...values,
        quantity: values.requestedQuantity
      },
      {
        rateFrom: null,
        rateTo: null
      }
    );

    if (data) {
      this.props.calculateByDays(data);
    }
  }

  getAssignmentsItems(newAssignments) {
    const { assignments } = this.state;

    const mappedAssignments = newAssignments.map(newAssignment => {
      const existingAssignment = assignments.find(
        assignment =>
          assignment.serviceRequestId === newAssignment.serviceRequestId
      );
      return {
        ...newAssignment,
        id: existingAssignment ? existingAssignment.id : ShortId.generate()
      };
    });

    const newActiveAssignments = assignments.filter(
      assignment => assignment.new
    );

    return [...mappedAssignments, ...newActiveAssignments];
  }

  dataStateChange = event => {
    this.setState({
      dataState: event.data
    });
  };

  getAssignments() {
    return this.state.assignments.map((item, index) => ({
      ...item,
      index
    }));
  }

  addAssignment = () => {
    this.setState({
      assignments: [
        ...this.state.assignments,
        { ...ASSIGNMENT_SHAPE, id: ShortId.generate() }
      ]
    });
  };

  removeAssignment(id) {
    const assignments = this.state.assignments.filter(
      assignment => assignment.id !== id
    );

    this.setState({
      assignments
    });
  }

  assign = (values, { setSubmitting }) => {
    const { t } = this.props;

    const data = {
      ...values,
      parentServiceRequestId: this.props.match.params.serviceRequestId
    };
    let updateFn = this.props.splitAssignment;

    if (data.update) {
      updateFn = this.props.updateSplitted;
    } else if (data.send) {
      updateFn = this.props.splitAndSendAssignment;
    }

    updateFn(data).then(async response => {
      if (response.success) {
        if (!data.update) {
          this.removeAssignment(data.id);
        }

        Toast.success(
          data.update
            ? t('Assignment updated successfully')
            : t('Assignment successfully saved')
        );

        await this.getAssignmentServiceRequest();
      } else if (response.payload) {
        Toast.warning(response.payload.message);
      } else {
        Toast.warning(t('Cannot assign subcontractor'));
      }
      setSubmitting(false);
    });
  };

  openAppointDialog = () => {
    this.setState({
      hasAppointment: true
    });
  };

  flattenUnitsTree(array, level = 0) {
    if (!Array.isArray(array)) {
      return [];
    }

    return array.reduce((result, item) => {
      result.push({
        unitName: `${'\xa0\xa0\xa0'.repeat(level)}${item.title}`,
        unitId: item.unitId
      });

      if (Array.isArray(item.units)) {
        result = result.concat(this.flattenUnitsTree(item.units, level + 1));
      }

      return result;
    }, []);
  }

  getUnitOptions = units => {
    const { t } = this.props;
    if (units && units.length) {
      return []
        .concat(
          [
            {
              unitName: t('Select sub-unit'),
              value: ''
            }
          ],
          units
        )
        .map(unit => {
          return (
            <option key={ShortId.generate()} value={unit.unitId}>
              {unit.unitName}
            </option>
          );
        });
    }
    return <option value="">{t('There are no sub-units')}</option>;
  };

  getItem(key) {
    const { item } = this.state;
    return item[key] ? item[key] : '-';
  }

  updateAppointment = values => {
    const { appoint, t } = this.props;
    if (values.appointedUnitId) {
      appoint(
        this.props.match.params.serviceRequestId,
        values.appointedUnitId
      ).then(response => {
        if (response.success) {
          Toast.success(t('Appointed unit successfully'));
          this.getAssignmentServiceRequest();
        } else if (response.payload) {
          Toast.warning(response.payload.message);
        }
      });
    } else {
      Toast.error('Unit not selected.');
    }
  };

  removeAppointment = () => {
    const { removeAppointment, t } = this.props;
    removeAppointment(this.props.match.params.serviceRequestId).then(
      response => {
        if (response.success) {
          Toast.success(t('Removed appointed unit successfully'));
          this.setState({ hasAppointment: false }, this.forceUpdate());
          this.forceUpdate();
          this.getAssignmentServiceRequest();
        } else if (response.payload) {
          Toast.warning(response.payload.message);
        }
      }
    );
  };

  getDelivery() {
    const { item } = this.state;

    return {
      deliveryQuantity:
        objectPath.get(item, 'assignmentResponse.response.deliveryQuantity') ||
        0,
      unitPrice:
        objectPath.get(item, 'assignmentResponse.response.unitPrice') || 0,
      totalPrice:
        objectPath.get(item, 'assignmentResponse.response.totalPrice') || 0,
      vatCode:
        objectPath.get(item, 'assignmentResponse.response.vatCode') || '',
      vatValue:
        objectPath.get(item, 'assignmentResponse.response.vatValue') || '',
      grossAmount:
        objectPath.get(item, 'assignmentResponse.response.grossAmount') || 0,
      comment:
        objectPath.get(item, 'assignmentResponse.response.comment') || '',
      files: objectPath.get(item, 'assignmentResponse.response.files') || []
    };
  }

  updateResponse = (values, { setSubmitting }) => {
    const { t, checkResponseSuggestion, match } = this.props;

    const data = {
      serviceRequestId: match.params.serviceRequestId,
      delivery: values.canDeliveryQuantity,
      isDraft: values.isDraft
    };

    checkResponseSuggestion(data).then(async response => {
      if (!response.success || !response.payload) {
        Toast.warning(t('Cannot send response'));
        setSubmitting(false);
        return;
      }

      if (!response.payload.show) {
        this.sendResponse(values, setSubmitting);
        return;
      }

      Confirm(
        'Attention',
        response.payload.message,
        () => {
          this.sendResponse(values, setSubmitting);
        },
        () => {
          setSubmitting(false);
        },
        'send response',
        'cancel'
      );
    });
  };

  sendResponse = (values, setSubmitting) => {
    const { t, updateAssignmentResponse } = this.props;
    const data = {
      ...values,
      serviceRequestId: this.props.match.params.serviceRequestId,
      attachments: values.attachments.map(attachment => {
        return attachment.attachmentId;
      })
    };

    updateAssignmentResponse(data).then(response => {
      if (response.success) {
        if (response.payload.isDraft) {
          Toast.success(t('Response draft saved successfully'));
        } else {
          Toast.success(t('Response sent successfully'));
        }
        this.getAssignmentServiceRequest();
      } else if (response.payload && response.payload.message) {
        Toast.warning(response.payload.message);
      } else {
        Toast.warning(t('Cannot save response'));
      }

      setSubmitting(false);
    });
  };

  onDeliveryRollback = () => {
    const { t, rollbackDelivery, match } = this.props;

    rollbackDelivery(match.params.serviceRequestId).then(response => {
      if (response.success) {
        Toast.success(t('Delivery information updated successfully'));
        this.getAssignmentServiceRequest();
      } else if (response.payload) {
        Toast.warning(response.payload.message);
      }
    });
  };

  updateDelivery = (values, { setSubmitting }) => {
    const { t, updateAssignmentDelivery } = this.props;
    const data = {
      ...values,
      serviceRequestId: this.props.match.params.serviceRequestId
    };

    updateAssignmentDelivery(data).then(response => {
      if (response.success) {
        this.getAssignmentServiceRequest();
        Toast.success(t('Service delivery confirmed successfully'));
      } else if (response.payload) {
        Toast.warning(response.payload.message);
      } else {
        Toast.warning(t('Cannot confirm delivery'));
      }

      setSubmitting(false);
    });
  };

  undo = serviceRequestId => {
    const { t, splittedUndo } = this.props;

    splittedUndo(serviceRequestId).then(response => {
      if (response.success) {
        Toast.success(t('Assignment updated successfully'));
        this.getAssignmentServiceRequest();
      } else if (response.payload) {
        Toast.warning(response.payload.message);
      } else {
        Toast.success(t('Cannot update assignment'));
      }
    });
  };

  delete = serviceRequestId => {
    const { t, splittedRemove } = this.props;

    splittedRemove(serviceRequestId).then(response => {
      if (response.success) {
        Toast.success(t('Assignment updated successfully'));
        this.getAssignmentServiceRequest();
      } else if (response.payload) {
        Toast.warning(response.payload.message);
      } else {
        Toast.success(t('Cannot update assignment'));
      }
    });
  };

  resend = serviceRequestId => {
    const { t, splittedResend } = this.props;

    splittedResend(serviceRequestId).then(response => {
      if (response.success) {
        Toast.success(t('Email sent successfully'));
      } else if (response.payload) {
        Toast.warning(response.payload.message);
      } else {
        Toast.success(t('Cannot send email'));
      }
    });
  };

  getTotalDeliveryQuantity() {
    const assignments = this.getAssignments();
    return assignments.reduce((results, { deliveryQuantity, statusId }) => {
      if (
        statusId === 2 ||
        deliveryQuantity === '' ||
        isNaN(deliveryQuantity)
      ) {
        return results;
      }
      return results + deliveryQuantity;
    }, 0);
  }

  getTotalCost() {
    const assignments = this.getAssignments();
    return assignments.reduce((results, { totalPrice, statusId }) => {
      if (statusId === 2 || totalPrice === '' || isNaN(totalPrice)) {
        return results;
      }
      return results + totalPrice;
    }, 0);
  }

  getTotalGrossAmount() {
    const assignments = this.getAssignments();
    return assignments.reduce(
      (results, { valueAddedTaxId, totalPrice, statusId }) => {
        if (statusId === 2) {
          return results;
        }

        return (
          results +
          totalPrice *
            (this.getSelectedVat(valueAddedTaxId).percentage / 100 + 1)
        );
      },
      0
    );
  }

  getSelectedVat = id => {
    const vat = this.props.valueAddedTaxes.find(vat => {
      if (id) {
        return vat.valueAddedTaxId === id;
      }

      return vat.isDefault;
    });
    return vat ? vat : {};
  };

  onResponseFileDialogOpen = () => {
    this.setState({
      responseFileDialogOpen: true
    });
  };

  onDeliveryFileDialogOpen = () => {
    this.setState({
      deliveryFileDialogOpen: true
    });
  };

  onResponseFileDialogClose = () => {
    this.setState({
      responseFileDialogOpen: false
    });
  };

  onDeliveryFileDialogClose = () => {
    this.setState({
      deliveryFileDialogOpen: false
    });
  };

  handleFileUpload = value => {
    return this.props.uploadAttachments({ attachments: value });
  };

  getBackNavigationUrl = navContext => {
    if (navContext === SERVICE_REQUEST_NAVIGATION_ORIGIN.AnnexOverview) {
      return '/annex-overview';
    } else if (navContext === SERVICE_REQUEST_NAVIGATION_ORIGIN.Orders) {
      return '/orders';
    } else {
      return '/annex-overview';
    }
  };

  updateAssignmentDeliveryFinalizedStatus = (value, callback) => {
    const { match, t } = this.props;
    this.props
      .updateAssignmentDeliveryFinalizedStatus(match.params.serviceRequestId)
      .then(response => {
        if (response.success) {
          if (value) {
            Toast.success(t('Delivery successfully marked as finalized'));
          } else {
            Toast.success(t('Delivery successfully marked as not finalized'));
          }
          callback();
        } else if (response.payload) {
          Toast.warning(response.payload.message);
        }
      });
  };

  getDiscussionPath() {
    const { match } = this.props;
    return `${match.url}/discussion`;
  }

  render() {
    const {
      t,
      item,
      match,
      hasUserPermission,
      calculateByDaysResults,
      units
    } = this.props;

    if (!item || !item.serviceRequestId) {
      return null;
    }

    const assignments = this.getAssignments();

    const disableAssignmentPosition =
      hasUserPermission(USER_ROLES.READONLY) ||
      hasUserPermission(USER_ROLES.HOST_UNIT) ||
      item.statusId === 2;

    const disabled =
      hasUserPermission(USER_ROLES.READONLY) ||
      hasUserPermission(USER_ROLES.HOST_UNIT) ||
      item.statusId === 2 ||
      (!item.canCreateAssignments && !item.isCategory);
    const isCategory = item.isCategory;
    const dateDifference =
      calculateByDaysResults && calculateByDaysResults.differenceInDays;
    const editedFields = objectPath.get(item, 'editedFields', []);
    return (
      <div className="request-information" ref={this.form}>
        <ServiceRequestNavContext.Consumer>
          {nav =>
            nav.serviceRequestId !== match.params.serviceRequestId &&
            nav.setServiceRequestId(match.params.serviceRequestId)
          }
        </ServiceRequestNavContext.Consumer>
        <ServiceRequestNavContext.Consumer>
          {nav =>
            this.props.withEscNavigationCallback(
              this.navigateBack(nav.serviceRequestNavigation)
            )
          }
        </ServiceRequestNavContext.Consumer>
        <div className="request-information--heading">
          <ServiceRequestNavContext.Consumer>
            {nav => (
              <PageHeader
                to={this.getBackNavigationUrl(nav.serviceRequestNavigation)}
                title={
                  t(
                    isCategory
                      ? 'Category request information'
                      : 'Request information'
                  ) + ' - '
                }
                unit={{
                  code: item.partyCode,
                  name: item.unitName,
                  number: item.requestNumber
                }}
                status={{
                  name: item.statusGridName,
                  id: item.statusId
                }}
                accounting={{
                  positionNumber: item.positionNumber,
                  purchaseOrderNumber: item.purchaseOrderNumber
                }}
              />
            )}
          </ServiceRequestNavContext.Consumer>

          <PageLinks
            serviceRequestId={match.params.serviceRequestId}
            discussion={{
              status: item.discussionStatus,
              initials: t('HN'),
              onDiscussionClose: this.loadComponentData,
              isSeen: item.isLastDiscussionSeen
            }}
            discussionPath={this.getDiscussionPath()}
          />
        </div>
        <div className="request-information--panels">
          <Panel>
            <div className="request-information--panel">
              <H2>{t('Item information')}</H2>
              <div className="request-information--info-panel">
                <InputLabel>{t('Service information')}</InputLabel>
                <InfoPanel
                  title={`${item.code} ${item.name}`}
                  titleIndicationMessage={FieldHistoryMessage(
                    'ServiceName',
                    editedFields
                  )}
                  detailsIndicationMessage={FieldHistoryMessage(
                    'ServiceDescription',
                    editedFields
                  )}
                >
                  {item.description}
                </InfoPanel>
              </div>

              <div className="request-information--row">
                <div>
                  <InputLabel
                    indicationMessage={FieldHistoryMessage(
                      'AnnexName',
                      editedFields
                    )}
                  >
                    {t('Annex')}
                  </InputLabel>
                  <div>{this.getItem('annexName')}</div>
                </div>
                <div>
                  <InputLabel
                    indicationMessage={FieldHistoryMessage(
                      'GroupName',
                      editedFields
                    )}
                  >
                    {t('Group')}
                  </InputLabel>
                  <div>{this.getItem('groupName')}</div>
                </div>
                <div>
                  <InputLabel>{t('Attachments')}</InputLabel>
                  <div>
                    {objectPath.get(item, 'attachments', []).length > 0
                      ? item.attachments.map(attachment => {
                          return (
                            <File
                              key={ShortId.generate()}
                              name={attachment.name}
                              attachmentId={attachment.attachmentId}
                            />
                          );
                        })
                      : '-'}
                  </div>
                </div>
              </div>

              <div className="request-information--row">
                <div>
                  <InputLabel
                    indicationMessage={FieldHistoryMessage(
                      'Comment',
                      editedFields
                    )}
                  >
                    {t('Comments')}
                  </InputLabel>
                  <div>
                    {item.comment &&
                    objectPath.get(item, 'comment', '').length > 0
                      ? item.comment
                      : '-'}
                  </div>
                </div>
              </div>
            </div>
          </Panel>

          <Panel>
            <div className="request-information--panel">
              <H2>{t('Delivery information')}</H2>

              <div className="request-information--row">
                <div>
                  <InputLabel
                    indicationMessage={FieldHistoryMessage(
                      'RequestedQuantity',
                      editedFields,
                      ['UnitOfMeasureName']
                    )}
                  >
                    {t('Req. quantity')}
                  </InputLabel>
                  <div>
                    <Quantity amount={this.getItem('requestedQuantity')} />{' '}
                    {this.getItem('unitOfMeasureName')}
                  </div>
                </div>
                <div>
                  <InputLabel
                    indicationMessage={FieldHistoryMessage(
                      'DateFrom',
                      editedFields
                    )}
                  >
                    {t('Delivery date (from)')}
                  </InputLabel>
                  <div>{format(this.getItem('dateFrom'), DATE_FORMAT)}</div>
                </div>
                <div>
                  <InputLabel
                    indicationMessage={FieldHistoryMessage(
                      'DateTo',
                      editedFields
                    )}
                  >
                    {t('Delivery date (to)')}
                  </InputLabel>
                  <div>{format(this.getItem('dateTo'), DATE_FORMAT)}</div>
                </div>
                <CalcByDays
                  name="isCalculatedByDays"
                  component={Switch}
                  disabled={true}
                  checked={item.isCalculatedByDays}
                  indicationMessage={FieldHistoryMessage(
                    'IsCalculatedByDays',
                    editedFields
                  )}
                  differenceInDays={dateDifference}
                />
              </div>

              <div className="request-information--row">
                <div>
                  <div className="request-information--row">
                    <div>
                      <InputLabel
                        indicationMessage={FieldHistoryMessage(
                          'LocationFrom',
                          editedFields
                        )}
                      >
                        {item.isTransportation
                          ? t('Pickup location')
                          : t('Delivery location')}
                      </InputLabel>
                      <div>{this.getItem('locationNameFrom')}</div>
                    </div>
                  </div>
                  <div className="request-information--row">
                    <div>
                      <InputLabel
                        indicationMessage={FieldHistoryMessage(
                          'RegionNameFrom',
                          editedFields
                        )}
                      >
                        {t('Region')}
                      </InputLabel>
                      <div>{this.getItem('regionNameFrom')}</div>
                    </div>
                    <div>
                      <InputLabel
                        indicationMessage={FieldHistoryMessage(
                          'SiteFrom',
                          editedFields
                        )}
                      >
                        {t('Site')}
                      </InputLabel>
                      <div>{this.getItem('siteFrom')}</div>
                    </div>
                  </div>

                  <div className="request-information--row">
                    <div>
                      <InputLabel
                        indicationMessage={FieldHistoryMessage(
                          'LatitudeFrom',
                          editedFields
                        )}
                      >
                        {t('Latitude')}
                      </InputLabel>
                      <div>{this.getItem('latitudeFrom')}</div>
                    </div>
                    <div>
                      <InputLabel
                        indicationMessage={FieldHistoryMessage(
                          'LongitudeFrom',
                          editedFields
                        )}
                      >
                        {t('Longitude')}
                      </InputLabel>
                      <div>{this.getItem('longitudeFrom')}</div>
                    </div>
                  </div>

                  <div className="request-information--row">
                    <div>
                      <InputLabel
                        indicationMessage={FieldHistoryMessage(
                          'LocationCommentFrom',
                          editedFields
                        )}
                      >
                        {t('Location comment')}
                      </InputLabel>
                      <div>{this.getItem('locationCommentFrom')}</div>
                    </div>
                  </div>
                </div>

                {item.isTransportation && (
                  <div>
                    <div className="request-information--row">
                      <div>
                        <InputLabel
                          indicationMessage={FieldHistoryMessage(
                            'LocationTo',
                            editedFields
                          )}
                        >
                          {t('Delivery Location')}
                        </InputLabel>
                        <div>{this.getItem('locationName')}</div>
                      </div>
                    </div>
                    <div>
                      <div className="request-information--row">
                        <div>
                          <InputLabel
                            indicationMessage={FieldHistoryMessage(
                              'RegionNameTo',
                              editedFields
                            )}
                          >
                            {t('Region')}
                          </InputLabel>
                          <div>{this.getItem('regionName')}</div>
                        </div>
                        <div>
                          <InputLabel
                            indicationMessage={FieldHistoryMessage(
                              'SiteTo',
                              editedFields
                            )}
                          >
                            {t('Site')}
                          </InputLabel>
                          <div>{this.getItem('site')}</div>
                        </div>
                      </div>

                      <div className="request-information--row">
                        <div>
                          <InputLabel
                            indicationMessage={FieldHistoryMessage(
                              'LatitudeTo',
                              editedFields
                            )}
                          >
                            {t('Latitude')}
                          </InputLabel>
                          <div>{this.getItem('latitude')}</div>
                        </div>
                        <div>
                          <InputLabel
                            indicationMessage={FieldHistoryMessage(
                              'LongitudeTo',
                              editedFields
                            )}
                          >
                            {t('Longitude')}
                          </InputLabel>
                          <div>{this.getItem('longitude')}</div>
                        </div>
                      </div>

                      <div className="request-information--row">
                        <div>
                          <InputLabel
                            indicationMessage={FieldHistoryMessage(
                              'LocationCommentTo',
                              editedFields
                            )}
                          >
                            {t('Location comment')}
                          </InputLabel>
                          <div>{this.getItem('locationComment')}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="request-information--cost-panel">
                  <BluePanel>
                    <div className="item">
                      {t('Total quantity')}
                      <InputLabel
                        indicationMessage={FieldHistoryMessage(
                          'CalculatedTotalQuantity',
                          editedFields
                        )}
                      />
                      {item.isCalculatedByDays ? (
                        <strong>
                          {item ? (
                            <Quantity
                              amount={item.requestedQuantity * dateDifference}
                            />
                          ) : (
                            ''
                          )}
                        </strong>
                      ) : (
                        <strong>
                          {item ? (
                            <Quantity amount={item.requestedQuantity} />
                          ) : (
                            ''
                          )}
                        </strong>
                      )}
                    </div>
                  </BluePanel>
                </div>
              </div>
            </div>
          </Panel>
        </div>
        {!isCategory && (
          <div className="request-information--assignment">
            {(!disabled || (assignments.length > 0 && disabled)) && (
              <H2>{t('Assignments')}</H2>
            )}
            {assignments.length > 0 && (
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('Subcontractor.')}</th>
                    <th>{t('Status.')}</th>
                    <th>{t('Req. q-ty')}</th>
                    <th>{t('Delivery q-ty')}</th>
                    <th>{t('Price')}</th>
                    <th>{t('Total Cost')}</th>
                    <th>{t('VAT')}</th>
                    <th>{t('Gross amount')}</th>
                    <th>{t('PO No.')}</th>
                    <th>{t('Pos. No.')}</th>
                    <th>{t('Files')}</th>
                    <th />
                    {!disabled && <th />}
                  </tr>
                </thead>
                <tbody>
                  {assignments.map(assignment => {
                    const defaultVat =
                      this.props.valueAddedTaxes.find(vat => vat.isDefault) ||
                      {};

                    if (this.props.valueAddedTaxes.length === 0) {
                      return null;
                    }

                    const initialValues = {
                      ...assignment,
                      valueAddedTaxId: assignment.valueAddedTaxId
                        ? assignment.valueAddedTaxId
                        : defaultVat.valueAddedTaxId,
                      unitPriceDisplay: assignment.unitPrice,
                      totalPriceDisplay: assignment.totalPrice
                    };

                    return (
                      <Formik
                        key={assignment.id}
                        validateOnBlur={false}
                        enableReinitialize={true}
                        validateOnChange={false}
                        validationSchema={this.assignmentValidationSchema}
                        initialValues={initialValues}
                        onSubmit={this.assign}
                      >
                        {({
                          handleSubmit,
                          setFieldValue,
                          isSubmitting,
                          ...rest
                        }) => {
                          const removed = assignment.statusId === 2;
                          const assignmentValues = { ...rest.values };

                          delete assignmentValues.expanded;

                          return (
                            <>
                              <tr
                                className={classnames({
                                  'request-information--assignment-removed': removed
                                })}
                              >
                                <td className="request-information--edit-cell--subcontractor">
                                  {!assignment.new && (
                                    <>
                                      {
                                        <Tooltip
                                          tooltip={assignment.requestNumber}
                                          position="top"
                                        >
                                          {assignment.subcontractorName}
                                        </Tooltip>
                                      }
                                    </>
                                  )}
                                  {assignment.new && (
                                    <Field
                                      setFieldValue={setFieldValue}
                                      name="subcontractorId"
                                      component={Dropdown}
                                      disabled={disabled || removed}
                                    >
                                      <option value="">
                                        {this.props.subcontractors.length
                                          ? t('Select subcontractor')
                                          : t('There are no Subcontractors')}
                                      </option>
                                      {this.props.subcontractors.map(
                                        subcontractor => (
                                          <option
                                            key={subcontractor.id}
                                            value={subcontractor.id}
                                          >
                                            {subcontractor.name}
                                          </option>
                                        )
                                      )}
                                    </Field>
                                  )}
                                </td>
                                <td className="request-information--edit-cell--status">
                                  <Status
                                    name={rest.values.statusGridName}
                                    type={rest.values.statusId}
                                  />
                                </td>
                                <td className="request-information--edit-cell">
                                  <Field
                                    noMargin
                                    disabled={
                                      removed ||
                                      disabled ||
                                      !assignment.canBeRemovedOrEdited
                                    }
                                    component={Input}
                                    type="number"
                                    setFieldValue={setFieldValue}
                                    name="requestedQuantity"
                                  />
                                </td>
                                <td className="request-information--edit-cell">
                                  <Field
                                    noMargin
                                    disabled={
                                      removed ||
                                      disabled ||
                                      !assignment.canBeRemovedOrEdited
                                    }
                                    component={Input}
                                    type="number"
                                    setFieldValue={setFieldValue}
                                    name="deliveryQuantity"
                                    onKeyUp={event => {
                                      if (rest.values.unitPrice) {
                                        setFieldValue(
                                          'totalPrice',
                                          event.target.value *
                                            rest.values.unitPrice
                                        );
                                        setFieldValue(
                                          'totalPriceDisplay',
                                          (
                                            event.target.value *
                                            rest.values.unitPrice
                                          ).toFixed(2)
                                        );
                                      }
                                    }}
                                  />
                                </td>
                                <td className="request-information--edit-cell">
                                  <Field
                                    noMargin
                                    disabled={
                                      removed ||
                                      disabled ||
                                      !assignment.canBeRemovedOrEdited
                                    }
                                    component={Input}
                                    type="number"
                                    setFieldValue={setFieldValue}
                                    name="unitPriceDisplay"
                                    onKeyUp={event => {
                                      setFieldValue(
                                        'unitPrice',
                                        event.target.value
                                      );

                                      if (rest.values.deliveryQuantity) {
                                        setFieldValue(
                                          'totalPrice',
                                          event.target.value *
                                            rest.values.deliveryQuantity
                                        );
                                        setFieldValue(
                                          'totalPriceDisplay',
                                          (
                                            event.target.value *
                                            rest.values.deliveryQuantity
                                          ).toFixed(2)
                                        );
                                      }
                                    }}
                                  />
                                  <Field name="unitPrice" type="hidden" />
                                </td>
                                <td className="request-information--edit-cell">
                                  <Field
                                    noMargin
                                    disabled={
                                      removed ||
                                      disabled ||
                                      !assignment.canBeRemovedOrEdited
                                    }
                                    component={Input}
                                    type="number"
                                    setFieldValue={setFieldValue}
                                    name="totalPriceDisplay"
                                    onKeyUp={event => {
                                      setFieldValue(
                                        'totalPrice',
                                        event.target.value
                                      );

                                      if (rest.values.deliveryQuantity) {
                                        setFieldValue(
                                          'unitPrice',
                                          event.target.value /
                                            rest.values.deliveryQuantity
                                        );
                                        setFieldValue(
                                          'unitPriceDisplay',
                                          (
                                            event.target.value /
                                            rest.values.deliveryQuantity
                                          ).toFixed(2)
                                        );
                                      }
                                    }}
                                  />
                                  <Field name="totalPrice" type="hidden" />
                                </td>

                                <td className="request-information--edit-cell vat-cell">
                                  <Field
                                    setFieldValue={setFieldValue}
                                    component={Dropdown}
                                    disabled={
                                      removed ||
                                      disabled ||
                                      !rest.values.requestedQuantity ||
                                      !rest.values.unitPrice ||
                                      !rest.values.totalPrice ||
                                      !assignment.canBeRemovedOrEdited
                                    }
                                    name="valueAddedTaxId"
                                  >
                                    {this.props.valueAddedTaxes.length ===
                                      0 && (
                                      <option value="">
                                        {t('There are no VAT codes')}
                                      </option>
                                    )}
                                    {this.props.valueAddedTaxes.map(vat => {
                                      return (
                                        <option
                                          key={ShortId.generate()}
                                          value={vat.valueAddedTaxId}
                                        >
                                          {vat.codeAndPercentage}
                                        </option>
                                      );
                                    })}
                                  </Field>
                                </td>
                                <td className="request-information--edit-cell">
                                  <Money
                                    amount={
                                      rest.values.totalPrice *
                                      (this.getSelectedVat(
                                        rest.values.valueAddedTaxId
                                      ).percentage /
                                        100 +
                                        1)
                                    }
                                  />
                                </td>
                                <td className="request-information--edit-cell">
                                  <Field
                                    noMargin
                                    component={Input}
                                    type="number"
                                    setFieldValue={setFieldValue}
                                    name="purchaseOrderNumber"
                                    disabled={
                                      disableAssignmentPosition ||
                                      removed ||
                                      assignment.isRemoved
                                    }
                                  />
                                </td>
                                <td className="request-information--edit-cell">
                                  <Field
                                    noMargin
                                    component={Input}
                                    type="number"
                                    setFieldValue={setFieldValue}
                                    name="positionNumber"
                                    disabled={
                                      disableAssignmentPosition ||
                                      removed ||
                                      assignment.isRemoved
                                    }
                                  />
                                </td>
                                <td>
                                  {assignmentValues.attachments.length > 0
                                    ? assignmentValues.attachments.map(
                                        attachment => {
                                          return (
                                            <File
                                              key={ShortId.generate()}
                                              name={attachment.name}
                                              attachmentId={
                                                attachment.attachmentId
                                              }
                                            />
                                          );
                                        }
                                      )
                                    : '-'}
                                </td>
                                <td
                                  className="request-information--last-cell"
                                  style={{
                                    width: '132px'
                                  }}
                                >
                                  <GhostButton
                                    iconLeft={<Icon icon="ArrowDown" />}
                                    type="button"
                                    className={classnames({
                                      expanded: rest.values.expanded
                                    })}
                                    onClick={() => {
                                      setFieldValue(
                                        'expanded',
                                        !rest.values.expanded
                                      );
                                    }}
                                  >
                                    {t('Comments')}
                                  </GhostButton>
                                </td>
                                <td className="request-information--transparent-cell">
                                  <div className="request-information--cell-row">
                                    {!disabled && assignment.new && (
                                      <>
                                        <RaisedButton
                                          onClick={async () => {
                                            await setFieldValue(
                                              'update',
                                              false
                                            );
                                            await setFieldValue('send', false);
                                            handleSubmit();
                                          }}
                                          type="button"
                                          disabled={isSubmitting}
                                        >
                                          {t('Assign')}
                                        </RaisedButton>
                                        <RaisedButton
                                          onClick={async () => {
                                            await setFieldValue(
                                              'update',
                                              false
                                            );
                                            await setFieldValue('send', true);
                                            handleSubmit();
                                          }}
                                          type="button"
                                          disabled={isSubmitting}
                                        >
                                          {t('Assign & send')}
                                        </RaisedButton>
                                        <GhostButton
                                          onClick={() => {
                                            this.removeAssignment(
                                              assignment.id
                                            );
                                          }}
                                          type="button"
                                        >
                                          {t('Cancel')}
                                        </GhostButton>
                                      </>
                                    )}

                                    {!disableAssignmentPosition &&
                                      !assignment.new &&
                                      !removed && (
                                        <GhostButton
                                          onClick={async () => {
                                            await setFieldValue('update', true);
                                            await setFieldValue('send', false);
                                            handleSubmit();
                                          }}
                                          type="button"
                                          disabled={isSubmitting}
                                        >
                                          {t('Update')}
                                        </GhostButton>
                                      )}

                                    {!disabled && !assignment.new && removed && (
                                      <GhostButton
                                        onClick={() => {
                                          this.undo(
                                            assignment.serviceRequestId
                                          );
                                        }}
                                        type="button"
                                      >
                                        {t('Undo')}
                                      </GhostButton>
                                    )}

                                    {!disabled && !assignment.new && !removed && (
                                      <GhostButton
                                        disabled={
                                          !assignment.canBeRemovedOrEdited
                                        }
                                        className="delete-button"
                                        onClick={() => {
                                          this.delete(
                                            assignment.serviceRequestId
                                          );
                                        }}
                                        type="button"
                                        iconLeft={<Icon icon="delete" />}
                                      />
                                    )}
                                  </div>
                                </td>
                              </tr>

                              {rest.values.expanded && (
                                <tr>
                                  <td className="k-detail-cell" colSpan={12}>
                                    <div
                                      className={classnames(
                                        'request-information--comments',
                                        {
                                          'request-information--comments-removed': removed
                                        }
                                      )}
                                    >
                                      <div>
                                        <Field
                                          component={Input}
                                          name="myComment"
                                          setFieldValue={setFieldValue}
                                          disabled={
                                            disabled ||
                                            removed ||
                                            !assignment.canBeRemovedOrEdited
                                          }
                                        >
                                          {t('My comments')}
                                        </Field>
                                      </div>
                                      <div>
                                        <Field
                                          component={Input}
                                          name="subcontractorComment"
                                          setFieldValue={setFieldValue}
                                          disabled={
                                            disabled ||
                                            removed ||
                                            !assignment.canBeRemovedOrEdited
                                          }
                                        >
                                          {t("Subcontractor's comments")}
                                        </Field>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="request-information--transparent-cell">
                                    <div className="request-information--cell-row">
                                      {!assignment.new && !removed && (
                                        <GhostButton
                                          onClick={() => {
                                            this.resend(
                                              assignment.serviceRequestId
                                            );
                                          }}
                                          type="button"
                                          disabled={isSubmitting}
                                        >
                                          {t('Resend')}
                                        </GhostButton>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </>
                          );
                        }}
                      </Formik>
                    );
                  })}
                  <tr>
                    <td colSpan="3">
                      <strong>{t('Total')}</strong>
                    </td>
                    <td colSpan="2">
                      <strong>
                        <Quantity amount={this.getTotalDeliveryQuantity()} />
                      </strong>
                    </td>
                    <td colSpan="2">
                      <strong>
                        <Money amount={this.getTotalCost()} />
                      </strong>
                    </td>
                    <td colSpan="5">
                      <strong>
                        <Money amount={this.getTotalGrossAmount()} />
                      </strong>
                    </td>
                  </tr>
                </tbody>
              </table>
            )}
            {!disabled && (
              <div className="request-information--assignment-action">
                <GhostButton
                  iconLeft={<Icon icon="Plus" />}
                  type="button"
                  onClick={this.addAssignment}
                >
                  {t('Add new assignment')}
                </GhostButton>
                <div className="request-information--assignment-action-appoint">
                  {!this.state.hasAppointment && (
                    <GhostButton
                      type="button"
                      onClick={() => {
                        this.openAppointDialog();
                      }}
                    >
                      {t('Appoint')}
                    </GhostButton>
                  )}
                  {this.state.hasAppointment && (
                    <Formik
                      validateOnBlur={false}
                      validateOnChange={false}
                      enableReinitialize={true}
                      initialValues={{
                        appointedUnitId: this.props.item.appointedUnitId,
                        appointedUnitName: this.props.item.appointedUnitName
                      }}
                      onSubmit={this.updateAppointment}
                    >
                      {({ setFieldValue }) => (
                        <Form>
                          <tr>
                            <td className="form">
                              <div>
                                <div>
                                  <div className="appointment-information--row">
                                    <p className="text">Appointed to</p>
                                    <div className="appointed-dropdown">
                                      <Field
                                        name={'appointedUnitId'}
                                        setFieldValue={setFieldValue}
                                        component={Dropdown}
                                        noMargin
                                      >
                                        {this.getUnitOptions(
                                          this.flattenUnitsTree(units)
                                        )}
                                      </Field>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </td>
                            &nbsp;&nbsp;
                            {!this.props.item.appointedUnitId && (
                              <div className="buttons">
                                <RaisedButton type="submit">
                                  {t('Appoint')}
                                </RaisedButton>
                                &nbsp;&nbsp;
                                <GhostButton
                                  type="button"
                                  onClick={() =>
                                    this.setState({ hasAppointment: false })
                                  }
                                >
                                  {t('Cancel')}
                                </GhostButton>
                              </div>
                            )}
                            {this.props.item.appointedUnitId && (
                              <div className="buttons">
                                <GhostButton type="submit">
                                  {t('Change')}
                                </GhostButton>
                                &nbsp;&nbsp;
                                <GhostButton
                                  onClick={() => {
                                    this.removeAppointment();
                                  }}
                                >
                                  <Icon icon="Delete" />
                                </GhostButton>
                              </div>
                            )}
                          </tr>
                        </Form>
                      )}
                    </Formik>
                  )}
                </div>
              </div>
            )}
            {item && (
              <div className="request-information--response">
                <H2>{t('Response to HN')}</H2>
                <AssignmentResponse
                  assignmentResponse={objectPath.get(item, 'response')}
                  delivery={item.delivery}
                  onSubmit={this.updateResponse}
                  onDeliverySubmit={this.updateDelivery}
                  vatOptions={this.props.valueAddedTaxes.map(vat => {
                    return (
                      <option
                        key={vat.valueAddedTaxId}
                        value={vat.valueAddedTaxId}
                      >
                        {vat.codeAndPercentage}
                      </option>
                    );
                  })}
                  getSelectedVat={this.getSelectedVat}
                  isFileDialogOpen={this.state.responseFileDialogOpen}
                  onFileDialogOpen={this.onResponseFileDialogOpen}
                  onFileDialogClose={this.onResponseFileDialogClose}
                  onFileSelect={this.handleFileUpload}
                  isDeliveryFileDialogOpen={this.state.deliveryFileDialogOpen}
                  onDeliveryFileDialogOpen={this.onDeliveryFileDialogOpen}
                  onDeliveryFileDialogClose={this.onDeliveryFileDialogClose}
                  onDeliveryFinalizedChange={
                    this.updateAssignmentDeliveryFinalizedStatus
                  }
                  onDeliveryRollback={this.onDeliveryRollback}
                />
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
}

RequestInformation.propTypes = {
  updateSplitted: PropTypes.func,
  getAssignmentServiceRequest: PropTypes.func,
  forSubmit: PropTypes.func,
  match: PropTypes.object,
  history: PropTypes.object,
  location: PropTypes.object,
  item: PropTypes.object,
  subcontractors: PropTypes.array,
  valueAddedTaxes: PropTypes.array,
  units: PropTypes.array,
  calculateByDays: PropTypes.func,
  calculateByDaysResults: PropTypes.object,
  t: PropTypes.func,
  splitAssignment: PropTypes.func,
  splitAndSendAssignment: PropTypes.func,
  splittedUndo: PropTypes.func,
  updateAssignmentResponse: PropTypes.func,
  splittedRemove: PropTypes.func,
  splittedResend: PropTypes.func,
  appoint: PropTypes.func,
  removeAppointment: PropTypes.func,
  updateServiceRequestAppoint: PropTypes.func,
  uploadAttachments: PropTypes.func,
  hasUserPermission: PropTypes.func,
  updateAssignmentDelivery: PropTypes.func,
  clearAssignmentServiceRequest: PropTypes.func,
  updateAssignmentDeliveryFinalizedStatus: PropTypes.func,
  rollbackDelivery: PropTypes.func,
  withEscNavigationCallback: PropTypes.func,
  checkResponseSuggestion: PropTypes.func,
  clearAttachments: PropTypes.func
};

const mapStateToProps = state => ({
  subcontractors: state.assignmentServiceRequest.forSubmit.subcontractors,
  valueAddedTaxes: state.assignmentServiceRequest.forSubmit.valueAddedTaxes,
  units: state.assignmentServiceRequest.forSubmit.units,
  item: state.assignmentServiceRequest.item,
  calculateByDaysResults: state.serviceRequests.calculateByDays,
  calculateByDaysData: state.serviceRequests.calculateByDays
});

const mapDispatchToProps = {
  updateAssignmentDelivery,
  updateSplitted,
  forSubmit,
  getAssignmentServiceRequest,
  splitAssignment,
  splitAndSendAssignment,
  updateAssignmentResponse,
  splittedUndo,
  splittedRemove,
  splittedResend,
  uploadAttachments,
  clearAttachments,
  clearAssignmentServiceRequest,
  updateAssignmentDeliveryFinalizedStatus,
  rollbackDelivery,
  appoint,
  removeAppointment,
  checkResponseSuggestion,
  calculateByDays
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  withTranslation()(
    withRouter(WithRoles(WithEscNavigation(RequestInformation)))
  )
);
